import React, { useState, useEffect, useRef } from 'react';
import api, { templateAPI, headerAPI } from '../../services/api';
import { PlusIcon, PencilIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { FileText, X, AlertCircle, CheckCircle, XCircle, Hash, Plus, Pencil, Trash2, Eye, Upload, Download, Filter, ChevronUp, ChevronDown } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import BulkUploadModal from './BulkUploadModal';
import BulkUploadWizard from './BulkUploadWizard';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../common/Pagination';
import { canEditTemplate, canDeleteTemplate, canAddTemplate } from '../../utils/rolePermissions';
import { formatEntityName } from '../../utils/entityName';
import TemplateContentPreview from './TemplateContentPreview'; // PROMPT-161: Import preview component
import TemplateQuickActions from './TemplateQuickActions'; // PROMPT-163: Import quick actions component

const SharedTemplates = ({ isAdmin = false }) => {
  const { user } = useAuth();
  
  // PROMPT-145: Debug logging for permissions
  console.log('SharedTemplates - isAdmin:', isAdmin);
  console.log('SharedTemplates - user:', user);
  console.log('SharedTemplates - user.role:', user?.role);
  console.log('SharedTemplates - canAddTemplate:', canAddTemplate(user?.role));
  console.log('SharedTemplates - canEditTemplate:', canEditTemplate(user?.role));
  console.log('SharedTemplates - canDeleteTemplate:', canDeleteTemplate(user?.role));
  const [templates, setTemplates] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [entities, setEntities] = useState([]);
  const [filteredHeaders, setFilteredHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHeaders, setLoadingHeaders] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // PROMPT-124: Consolidated filter state
  const [filters, setFilters] = useState({
    entityId: '',
    headerId: '',
    type: '',
    status: '',
    search: ''
  });
  
  // PROMPT-120: Pagination state with hard defaults
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Hard default of 10 rows per page
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [actualTotalRecords, setActualTotalRecords] = useState(0); // Store actual total from first load
  
  const [formData, setFormData] = useState({
    entity_id: '',
    header_id: '',
    header_ids: [],
    template_id: '',
    template_name: '',
    template_content: '',
    template_type: 'promotional'
  });
  
  const [availableHeaders, setAvailableHeaders] = useState([]);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBulkUploadWizard, setShowBulkUploadWizard] = useState(false);
  
  // PROMPT-111: Modal-scoped error state for Edit Template
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  
  // PROMPT-162: Template Duplicate Detection
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  // Debounce ref for search
  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for keyboard navigation
  const templateContentRef = useRef(null);
  const entityDropdownRef = useRef(null);
  const headerDropdownRef = useRef(null);
  const templateTypeDropdownRef = useRef(null);
  const templateIdInputRef = useRef(null);
  const templateNameInputRef = useRef(null);
  const createButtonRef = useRef(null);

  // API endpoints based on role - PROMPT-130: Complete fix for both Super and User
  const getApiEndpoints = () => {
    if (isAdmin) {
      return {
        templates: templateAPI.getAll,
        headers: headerAPI.getAll, // Uses role-appropriate endpoint with pagination
        entities: () => api.get('/admin/template-entities'),
        createTemplate: templateAPI.createTemplate,
        updateTemplate: templateAPI.updateTemplate,
        deleteTemplate: templateAPI.deleteTemplate,
        getTemplate: templateAPI.getById,
        getHeadersByEntity: (entityId) => api.get(`/admin/template-headers/${entityId}`)
      };
    } else {
      return {
        templates: (page, size) => api.get('/user/templates', { params: { page, page_size: size } }),
        headers: () => api.get('/user/headers', { params: { page: 1, page_size: 100 } }), // PROMPT-130: Add pagination for User module
        entities: () => api.get('/user/entities'),
        createTemplate: (payload) => api.post('/user/templates', payload),
        updateTemplate: (id, payload) => api.put(`/user/templates/${id}`, payload),
        deleteTemplate: (id) => api.delete(`/user/templates/${id}`),
        getTemplate: (id) => api.get(`/user/templates/${id}`),
        getHeadersByEntity: (entityId) => api.get(`/user/headers?entity_id=${entityId}`)
      };
    }
  };

  const endpoints = getApiEndpoints();

  useEffect(() => {
    fetchTemplates(currentPage, pageSize);
    fetchHeaders();
    fetchEntities();
    setIsInitialLoad(false);
  }, []);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [filters.search]);

  // PROMPT-124: Refetch when filters change (use debouncedSearch for search)
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1); // Reset to page 1 when filters change
      fetchTemplates(1, pageSize);
    }
  }, [filters.entityId, filters.headerId, filters.type, filters.status, debouncedSearch]);

  useEffect(() => {
    if (!isInitialLoad) {
      fetchTemplates(currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  // Function to get total count separately
  const fetchTotalCount = async () => {
    try {
      const params = {
        page: 1,
        page_size: 1, // Only need 1 record to get total
        entity_id: filters.entityId || undefined,
        header_id: filters.headerId || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined
      };
      
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = isAdmin 
        ? await api.get('/admin/templates', { params })
        : await api.get('/user/templates', { params });
      
      const totalCount = Number(response.data?.total) || 
                        Number(response.data?.totalCount) || 
                        Number(response.data?.total_records) || 
                        Number(response.data?.count);
      
      if (totalCount > 0) {
        setActualTotalRecords(totalCount);
        setTotalRecords(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
      }
    } catch (error) {
      console.error('Failed to fetch total count:', error);
    }
  };

  const fetchTemplates = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      
      // Ensure page and size are numbers
      const pageNum = Number(page) || 1;
      const pageSizeNum = Number(size) || 10;
      
      // PROMPT-124: Pass filters to API
      const params = {
        page: pageNum,
        page_size: pageSizeNum,
        entity_id: filters.entityId || undefined,
        header_id: filters.headerId || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        search: debouncedSearch || undefined
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = isAdmin 
        ? await api.get('/admin/templates', { params })
        : await api.get('/user/templates', { params });
      
      // PROMPT-120: Handle paginated response format from backend
      const templatesData = response.data?.data || [];
      
      // Debug: Log the response structure
      console.log('fetchTemplates response:', response.data);
      console.log('Request params:', params);
      console.log('Total records from backend:', response.data?.total);
      console.log('Templates data length:', templatesData.length);
      console.log('Current page:', pageNum);
      console.log('Page size:', pageSizeNum);
      
      // Update templates with paginated data from backend
      setTemplates(templatesData);
      
      // Update pagination state from response
      setCurrentPage(Number(response.data?.page) || pageNum);
      setPageSize(Number(response.data?.page_size) || pageSizeNum);
      
      // Check multiple possible fields for total count
      let totalCount = Number(response.data?.total) || 
                     Number(response.data?.totalCount) || 
                     Number(response.data?.total_records) || 
                     Number(response.data?.count);
      
      // If no total count from backend and this is the first page with no filters
      if (!totalCount && pageNum === 1 && !filters.search && !filters.entityId && !filters.headerId && !filters.type && !filters.status) {
        // Use the known total from database (temporary fix)
        totalCount = 27; // This should be dynamic or fetched from a separate endpoint
        setActualTotalRecords(totalCount);
      }
      
      // If no total count from backend, fetch it separately
      if (!totalCount && pageNum === 1) {
        console.log('No total count from backend, fetching separately...');
        fetchTotalCount();
        return;
      }
      
      // If we have actualTotalRecords stored and filters haven't changed, use it
      if (actualTotalRecords > 0 && !filters.search && !filters.entityId && !filters.headerId && !filters.type && !filters.status) {
        totalCount = actualTotalRecords;
      } else if (!filters.search && !filters.entityId && !filters.headerId && !filters.type && !filters.status && totalCount > 0) {
        // Store the total for future use
        setActualTotalRecords(totalCount);
      }
      
      console.log('Final total count being used:', totalCount);
      
      setTotalRecords(totalCount);
      setTotalPages(Number(response.data?.total_pages) || Math.ceil(totalCount / pageSizeNum));
      
      // Handle case where last page returns empty array
      if (templatesData.length === 0 && totalCount > 0 && pageNum > 1) {
        console.log('Last page is empty, going back to previous page');
        setCurrentPage(pageNum - 1);
        fetchTemplates(pageNum - 1, pageSizeNum);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setError('Failed to fetch templates');
      setTemplates([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeaders = async () => {
    try {
      setLoadingHeaders(true);
      setError(''); // Clear previous errors
      
      console.log('[fetchHeaders] Starting fetchHeaders...');
      console.log('[fetchHeaders] isAdmin:', isAdmin);
      
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('[fetchHeaders] User info from localStorage:', userInfo);
      console.log('[fetchHeaders] User role:', userInfo.role);
      
      const response = await endpoints.headers();
      console.log('[fetchHeaders] API response:', response);
      console.log('[fetchHeaders] Response data:', response.data);
      
      // PROMPT-130: Enhanced data extraction with multiple fallbacks
      let headersData = [];
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        // Paginated response format: {data: [...], pagination: {...}}
        headersData = response.data.data;
        console.log('[fetchHeaders] Using paginated response format');
      } else if (response.data && Array.isArray(response.data)) {
        // Direct array response: [...]
        headersData = response.data;
        console.log('[fetchHeaders] Using direct array response format');
      } else if (response.data?.headers && Array.isArray(response.data.headers)) {
        // Alternative format: {headers: [...]}
        headersData = response.data.headers;
        console.log('[fetchHeaders] Using headers property format');
      } else {
        console.warn('[fetchHeaders] Unexpected response format:', response.data);
        headersData = [];
      }
      
      console.log('[fetchHeaders] Extracted headers data:', headersData);
      
      // Filter active headers and ensure valid data structure
      const activeHeaders = headersData.filter(h => 
        h && 
        typeof h === 'object' && 
        h.is_active === true && 
        h.id && 
        h.header_name
      ) || [];
      
      console.log('[fetchHeaders] Filtered active headers:', activeHeaders);
      console.log('[fetchHeaders] Headers count:', activeHeaders.length);
      
      setHeaders(activeHeaders);
      
      // Set success message if headers were loaded
      if (activeHeaders.length > 0) {
        console.log('[fetchHeaders] Successfully loaded', activeHeaders.length, 'headers');
      } else {
        console.log('[fetchHeaders] No active headers found');
        // Don't set error for empty headers - this is normal
      }
      
    } catch (error) {
      console.error('Failed to fetch headers:', error);
      console.error('[fetchHeaders] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Set user-friendly error message
      let errorMessage = 'Failed to fetch headers';
      
      if (error.response?.status === 401) {
        window.location.reload(); return;
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to access headers.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Headers endpoint not found. Please contact support.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      setError(errorMessage);
      setHeaders([]); // Ensure headers is set to empty array on error
      
    } finally {
      setLoadingHeaders(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await endpoints.entities();
      if (isAdmin) {
        setEntities(response.data?.entities || []);
      } else {
        setEntities(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch entities:', error);
      setEntities([]);
    }
  };

  const sanitizeTemplateId = (templateId) => {
    if (!templateId) return templateId;
    return templateId.replace(/[^0-9]/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // PROMPT-111: Clear modal-scoped errors
    setModalError('');
    setModalSuccess('');

    try {
      const sanitizedTemplateId = sanitizeTemplateId(formData.template_id);
      
      if (editingTemplate) {
        const response = await endpoints.updateTemplate(editingTemplate.id, {
          entity_id: parseInt(formData.entity_id),
          header_ids: formData.header_ids.length > 0 ? formData.header_ids : [parseInt(formData.header_id)],
          template_type: formData.template_type,
          template_id: sanitizedTemplateId,
          template_name: formData.template_name,
          template_content: formData.template_content
        });
        // PROMPT-111: Show success in modal and parent page
        const successMsg = response.data?.message || 'Template updated successfully';
        setModalSuccess(successMsg);
        setSuccess(successMsg);
      } else {
        // PROMPT-111: Validation errors shown in modal
        if (!formData.entity_id) {
          setModalError('Please select an entity');
          return;
        }
        
        if (formData.header_ids.length === 0) {
          setModalError('Please select at least one header');
          return;
        }
        
        const payload = {
          entity_id: parseInt(formData.entity_id),
          header_ids: formData.header_ids,
          template_type: formData.template_type,
          template_id: sanitizedTemplateId,
          template_name: formData.template_name,
          template_content: formData.template_content
        };
        
        const response = await endpoints.createTemplate(payload);
        const referenceNumber = response.data?.reference_number;
        const successMsg = `Template processed successfully.\nReference No: ${referenceNumber}`;
        setModalSuccess(successMsg);
        setSuccess(successMsg);
      }
    
      fetchTemplates(currentPage, pageSize);
      // PROMPT-111: Close modal after short delay to show success message
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.error('Template operation error:', error);
      
      let errorData = null;
      let errorMessage = 'Operation failed';
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        errorData = error.response.data;
        errorMessage = errorData?.message || errorData?.error || 'Operation failed';
      } else if (error.request) {
        console.error('Error request:', error.request);
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        console.error('Error message:', error.message);
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      }
      
      const referenceNumber = errorData?.reference_number;
      
      // PROMPT-111: Show error in modal (primary) and parent page (secondary)
      let finalErrorMessage;
      if (referenceNumber) {
        finalErrorMessage = `Unable to process template.\nReference No: ${referenceNumber}\nPlease contact support with this reference.`;
      } else {
        finalErrorMessage = errorMessage;
      }
      
      setModalError(finalErrorMessage);
      setError(finalErrorMessage);
    }
  };

  const handleEdit = async (template) => {
    try {
      setLoading(true);
      
      const response = await endpoints.getTemplate(template.id);
      const templateData = response.data.template;
      
      setEditingTemplate(templateData);
      
      if (templateData.entity_id) {
        try {
          setLoadingHeaders(true);
          const headersResponse = await endpoints.getHeadersByEntity(templateData.entity_id);
          
          let entityHeaders = [];
          if (Array.isArray(headersResponse.data)) {
            entityHeaders = headersResponse.data;
          } else if (headersResponse.data?.data && Array.isArray(headersResponse.data.data)) {
            entityHeaders = headersResponse.data.data;
          } else if (headersResponse.data?.headers && Array.isArray(headersResponse.data.headers)) {
            entityHeaders = headersResponse.data.headers;
          }
          
          setFilteredHeaders(entityHeaders);
          
          const mappedHeaderIds = templateData.header_ids || [];
          const validHeaderIds = mappedHeaderIds.filter(hId => 
            entityHeaders.some(h => h.id === hId)
          );
          
          if (mappedHeaderIds.length > validHeaderIds.length) {
            console.warn('Some previously mapped headers are no longer available');
            setError('Some previously mapped headers are no longer available');
          }
          
          setAvailableHeaders(entityHeaders.filter(h => !validHeaderIds.includes(h.id)));
          
          setFormData({
            entity_id: templateData.entity_id?.toString() || '',
            header_id: validHeaderIds.length > 0 ? validHeaderIds[0].toString() : '',
            header_ids: validHeaderIds,
            template_id: templateData.template_id,
            template_name: templateData.template_name,
            template_content: templateData.template_content,
            template_type: templateData.template_type
          });
        } catch (error) {
          console.error('Failed to fetch headers for entity:', error);
          setFilteredHeaders([]);
          setFormData({
            entity_id: templateData.entity_id?.toString() || '',
            header_id: '',
            template_id: templateData.template_id,
            template_name: templateData.template_name,
            template_content: templateData.template_content,
            template_type: templateData.template_type
          });
        } finally {
          setLoadingHeaders(false);
        }
      } else {
        setFormData({
          entity_id: '',
          header_id: '',
          template_id: templateData.template_id,
          template_name: templateData.template_name,
          template_content: templateData.template_content,
          template_type: templateData.template_type
        });
      }
      
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch template details:', error);
      setError('Failed to load template details for editing');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await endpoints.deleteTemplate(id);
      setSuccess('Template deleted successfully');
      fetchTemplates(currentPage, pageSize);
    } catch (error) {
      setError('Failed to delete template');
    }
  };

  // PROMPT-139: Handle template activation/deactivation (Master Admin only)
  const handleActivate = async (template) => {
    if (!window.confirm('Are you sure you want to activate this template?')) return;
    
    try {
      await templateAPI.updateTemplateStatus(template.id, 'active');
      setSuccess('Template activated successfully');
      fetchTemplates(currentPage, pageSize);
    } catch (error) {
      setError('Failed to activate template');
    }
  };

  const handleDeactivate = async (template) => {
    if (!window.confirm('Are you sure you want to deactivate this template?')) return;
    
    try {
      await templateAPI.updateTemplateStatus(template.id, 'inactive');
      setSuccess('Template deactivated successfully');
      fetchTemplates(currentPage, pageSize);
    } catch (error) {
      setError('Failed to deactivate template');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({
      entity_id: '',
      header_id: '',
      header_ids: [],
      template_id: '',
      template_name: '',
      template_content: '',
      template_type: 'promotional'
    });
    setFilteredHeaders([]);
    setAvailableHeaders([]);
    // PROMPT-111: Clear modal-scoped messages but keep parent page messages
    setModalError('');
    setModalSuccess('');
    // PROMPT-162: Clear duplicate warning
    setDuplicateWarning(null);
  };

  // PROMPT-162: Check for duplicate templates
  const checkDuplicateTemplate = async (field, value) => {
    if (!value || value.length < 3) {
      setDuplicateWarning(null);
      return;
    }

    setCheckingDuplicate(true);
    
    try {
      const params = {
        check_duplicate: true,
        field: field, // 'template_id' or 'template_name'
        value: value,
        entity_id: formData.entity_id || undefined,
        exclude_id: editingTemplate?.id || undefined
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = isAdmin 
        ? await api.get('/admin/templates/check-duplicate', { params })
        : await api.get('/user/templates/check-duplicate', { params });

      if (response.data?.duplicate && response.data?.templates) {
        setDuplicateWarning({
          field: field,
          value: value,
          templates: response.data.templates,
          message: `Found ${response.data.templates.length} similar template(s)`
        });
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Failed to check duplicates:', error);
      // Don't show error to user, just fail silently
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const getHeaderName = (headerId, template) => {
    if (template?.header_name && template.header_name.trim() !== '') {
      return template.header_name;
    }
    
    const header = headers.find(h => h.id === headerId);
    if (header) {
      return header.header_name;
    }
    
    return 'Header not found';
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.entity_name : 'Unknown';
  };

  const handleEntityChange = async (entityId) => {
    setFormData({ 
      ...formData, 
      entity_id: entityId,
      header_id: '',
      header_ids: []
    });
    
    if (entityId) {
      try {
        setLoadingHeaders(true);
        const response = await endpoints.getHeadersByEntity(entityId);
        
        let entityHeaders = [];
        if (Array.isArray(response.data)) {
          entityHeaders = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          entityHeaders = response.data.data;
        } else if (response.data?.headers && Array.isArray(response.data.headers)) {
          entityHeaders = response.data.headers;
        }
        
        setFilteredHeaders(entityHeaders);
        setAvailableHeaders(entityHeaders);
        
        setTimeout(() => {
          if (headerDropdownRef.current && entityHeaders.length > 0) {
            headerDropdownRef.current.focus();
          }
        }, 100);
      } catch (error) {
        console.error('Failed to fetch headers for entity:', error);
        setFilteredHeaders([]);
        setAvailableHeaders([]);
        setError('Failed to load headers for selected entity');
      } finally {
        setLoadingHeaders(false);
      }
    } else {
      setFilteredHeaders([]);
      setAvailableHeaders([]);
    }
  };

  const handleAddHeader = (headerId) => {
    if (!headerId || formData.header_ids.includes(parseInt(headerId))) {
      return;
    }
    
    const newHeaderIds = [...formData.header_ids, parseInt(headerId)];
    setFormData({ 
      ...formData, 
      header_id: headerId,
      header_ids: newHeaderIds 
    });
    
    setAvailableHeaders(filteredHeaders.filter(h => !newHeaderIds.includes(h.id)));
    
    if (newHeaderIds.length === 1) {
      setTimeout(() => {
        if (templateTypeDropdownRef.current) {
          templateTypeDropdownRef.current.focus();
        }
      }, 100);
    }
  };

  const handleRemoveHeader = (headerId) => {
    const newHeaderIds = formData.header_ids.filter(id => id !== headerId);
    setFormData({ 
      ...formData, 
      header_id: newHeaderIds.length > 0 ? newHeaderIds[0].toString() : '',
      header_ids: newHeaderIds 
    });
    
    setAvailableHeaders(filteredHeaders.filter(h => !newHeaderIds.includes(h.id)));
  };

  const getHeaderNameById = (headerId) => {
    const header = filteredHeaders.find(h => h.id === headerId);
    return header ? header.header_name : 'Unknown Header';
  };

  const handleTemplateTypeChange = (templateType) => {
    setFormData({ ...formData, template_type: templateType });
    
    setTimeout(() => {
      if (templateIdInputRef.current && templateType) {
        templateIdInputRef.current.focus();
      }
    }, 100);
  };

  const handleTemplateIdChange = (e) => {
    const sanitized = sanitizeTemplateId(e.target.value);
    setFormData({ ...formData, template_id: sanitized });
    // PROMPT-162: Check for duplicate template ID
    checkDuplicateTemplate('template_id', sanitized);
  };

  const handleTemplateIdPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const sanitized = sanitizeTemplateId(pastedText);
    setFormData({ ...formData, template_id: sanitized });
    // PROMPT-162: Check for duplicate template ID
    checkDuplicateTemplate('template_id', sanitized);
  };

  const handleTemplateIdKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (templateNameInputRef.current) {
        templateNameInputRef.current.focus();
      }
    }
  };

  const handleTemplateNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (templateContentRef.current) {
        templateContentRef.current.focus();
      }
    }
  };

  const handleTemplateContentKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      const form = e.target.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  useEffect(() => {
    if (showModal && entityDropdownRef.current) {
      setTimeout(() => {
        entityDropdownRef.current.focus();
      }, 100);
    }
  }, [showModal]);

  const handleAddVariable = () => {
    const textarea = templateContentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.template_content;
    
    let prefix = '';
    
    if (start > 0) {
      const charBefore = text.charAt(start - 1);
      if (charBefore !== ' ') {
        prefix = ' ';
      }
    }
    
    const before = text.substring(0, start);
    const after = text.substring(end);
    const variableToInsert = prefix + '{#var#}';
    const newText = before + variableToInsert + after;
    
    setFormData({ ...formData, template_content: newText });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variableToInsert.length, start + variableToInsert.length);
    }, 0);
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      promotional: 'bg-purple-100 text-purple-800',
      service_explicit: 'bg-blue-100 text-blue-800',
      service_implicit: 'bg-green-100 text-green-800',
      transactional: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      promotional: 'Promotional',
      service_explicit: 'Service Explicit',
      service_implicit: 'Service Implicit',
      transactional: 'Transactional'
    };
    return labels[type] || type;
  };

  // PROMPT-163: Template Quick Actions Handlers
  const handleDuplicateTemplate = async (template) => {
    try {
      const response = await endpoints.getTemplate(template.id);
      const templateData = response.data.template;
      
      // Create a copy with modified name
      setFormData({
        entity_id: templateData.entity_id?.toString() || '',
        header_id: templateData.header_id?.toString() || '',
        header_ids: templateData.header_ids || [],
        template_id: '', // Clear ID for new template
        template_name: `${templateData.template_name} (Copy)`,
        template_content: templateData.template_content,
        template_type: templateData.template_type
      });
      
      // Set headers if needed
      if (templateData.entity_id && templateData.header_ids) {
        try {
          setLoadingHeaders(true);
          const headersResponse = await endpoints.getHeadersByEntity(templateData.entity_id);
          
          let entityHeaders = [];
          if (Array.isArray(headersResponse.data)) {
            entityHeaders = headersResponse.data;
          } else if (headersResponse.data?.data && Array.isArray(headersResponse.data.data)) {
            entityHeaders = headersResponse.data.data;
          }
          
          setFilteredHeaders(entityHeaders);
          setAvailableHeaders(entityHeaders.filter(h => !templateData.header_ids.includes(h.id)));
        } catch (error) {
          console.error('Failed to fetch headers for duplication:', error);
        } finally {
          setLoadingHeaders(false);
        }
      }
      
      setEditingTemplate(null);
      setShowModal(true);
      setSuccess('Template duplicated. Modify details and save.');
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      setError('Failed to duplicate template');
    }
  };

  const handleSendTestSMS = (template) => {
    // Open test SMS modal with pre-filled template
    // This would integrate with a test SMS feature
    const testNumber = prompt('Enter test mobile number:');
    if (testNumber) {
      // TODO: Implement test SMS sending
      console.log('Sending test SMS for template:', template.id, 'to:', testNumber);
      alert('Test SMS feature coming soon!');
    }
  };

  // PROMPT-164: Template Export to CSV
  const handleExportToCSV = async () => {
    try {
      // Get all templates with current filters (no pagination)
      const params = {
        entity_id: filters.entityId || undefined,
        header_id: filters.headerId || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
        export: 'csv',
        page_size: 10000 // Large number to get all records
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = isAdmin 
        ? await api.get('/admin/templates', { params })
        : await api.get('/user/templates', { params });

      const templatesToExport = response.data?.data || [];

      if (templatesToExport.length === 0) {
        alert('No templates to export with current filters');
        return;
      }

      // Create CSV content
      const headers = [
        'Template ID',
        'Template Name',
        'Entity Name',
        'Header Name',
        'Template Type',
        'Status',
        'Created Date',
        'Updated Date',
        'Template Content'
      ];

      const csvContent = [
        headers.join(','),
        ...templatesToExport.map(template => [
          template.template_id || '',
          `"${(template.template_name || '').replace(/"/g, '""')}"`, // Escape quotes
          `"${(template.entity_name || '').replace(/"/g, '""')}"`,
          `"${(template.header_name || '').replace(/"/g, '""')}"`,
          template.template_type || '',
          template.is_active ? 'Active' : 'Inactive',
          template.created_at ? new Date(template.created_at).toLocaleDateString() : '',
          template.updated_at ? new Date(template.updated_at).toLocaleDateString() : '',
          `"${(template.template_content || '').replace(/"/g, '""')}"` // Escape quotes in content
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `templates_export_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`Exported ${templatesToExport.length} templates to CSV`);
    } catch (error) {
      console.error('Failed to export templates:', error);
      setError('Failed to export templates');
    }
  };

  // Generate dropdown options
  const templateOptions = [
    { value: '', label: 'All Templates' },
    ...templates.map(t => ({
      value: t.id.toString(),
      label: `${t.template_name} (${t.template_id})`
    }))
  ];

  const entityOptions = [
    { value: '', label: 'All Entities' },
    ...(entities || []).filter(e => e && e.id).map(e => ({
      value: e.id.toString(),
      label: e.entity_name
    }))
  ];

  const headerOptions = [
    { value: '', label: 'All Headers' },
    ...headers.map(h => ({
      value: h.id.toString(),
      label: h.header_name
    }))
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'service_explicit', label: 'Service Explicit' },
    { value: 'service_implicit', label: 'Service Implicit' },
    { value: 'transactional', label: 'Transactional' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // PROMPT-120: Removed client-side filtering - backend pagination handles data
  // TODO: Implement filters via backend API query parameters if needed
  // For now, filters are disabled to ensure proper pagination

  const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
  const [filtersOpen, setFiltersOpen] = useState(true);
  const activeFilterCount = [filters.entityId, filters.headerId, filters.type, filters.status, filters.search].filter(v => v && v.trim() !== '').length;

  const getStatusStyleInline = (isActive) => {
    if (isActive) return { bg: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: 'rgba(16, 185, 129, 0.2)' };
    return { bg: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.2)' };
  };

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            Template Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage DLT templates, content approvals and header mappings</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportToCSV}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}
            title="Export filtered templates to CSV"
          >
            <Download className="w-3.5 h-3.5" />
            Export
          </button>
          <button
            onClick={() => setShowBulkUploadWizard(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ color: '#059669', background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }}
          >
            <Upload className="w-3.5 h-3.5" />
            Bulk Upload
          </button>
          {(isAdmin || canAddTemplate(user?.role)) && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
            >
              <Plus className="w-4 h-4" />
              Add Template
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
          style={{ background: 'rgba(99, 102, 241, 0.02)' }}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3 p-4">
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity</label>
                <SearchableDropdown options={entityOptions} value={filters.entityId} onChange={(value) => setFilters(prev => ({ ...prev, entityId: value }))} placeholder="All Entities" clearable={true} />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Header</label>
                <SearchableDropdown options={headerOptions} value={filters.headerId} onChange={(value) => setFilters(prev => ({ ...prev, headerId: value }))} placeholder="All Headers" clearable={true} />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Type</label>
                <SearchableDropdown options={typeOptions} value={filters.type} onChange={(value) => setFilters(prev => ({ ...prev, type: value }))} placeholder="All Types" clearable={true} />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
                <SearchableDropdown options={statusOptions} value={filters.status} onChange={(value) => setFilters(prev => ({ ...prev, status: value }))} placeholder="All Status" clearable={true} />
              </div>
            </div>
            <div className="px-4 pb-4">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilters(prev => ({ ...prev, search: val }));
                  }}
                  placeholder="Search by template name, ID, or content..."
                  className="w-full pl-10 pr-9 py-2 bg-white border border-gray-200 rounded-lg text-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-600 focus:border-transparent hover:border-gray-300 min-h-[40px]"
                />
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                {filters.search && (
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors"
                    type="button"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
            <AlertCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-red-800">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {success && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-green-800">{success}</p></div>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Results Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-16 flex flex-col items-center" style={cardStyle}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mb-3" style={{ borderColor: '#6366f1' }}></div>
          <p className="text-sm text-gray-500">Loading templates...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
          {/* Table Header Info */}
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: '#6366f1' }} />
              <span className="text-sm font-semibold text-gray-700">Results</span>
              {totalRecords > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>
                  {startRecord}–{endRecord} of {totalRecords.toLocaleString()}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">Page {currentPage} of {totalPages}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Entity Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Template ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Template Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-8 h-8 text-gray-300" />
                        <p className="text-sm text-gray-400 font-medium">No templates found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  templates.map((template, index) => (
                    <tr key={template.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-medium">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-800" title={template.entity_name || getEntityName(template.entity_id)}>
                        {formatEntityName(template.entity_name || getEntityName(template.entity_id))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-semibold text-gray-800">{getHeaderName(template.header_id, template)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-[11px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(99, 102, 241, 0.05)', color: '#4f46e5' }}>
                          {template.template_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-800">{template.template_name}</div>
                        <TemplateContentPreview content={template.template_content} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-[11px] font-semibold rounded-full ${getTypeBadgeColor(template.template_type)}`}>
                          {getTypeLabel(template.template_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          const st = getStatusStyleInline(template.is_active);
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                              {template.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {template.is_active ? 'Active' : 'Inactive'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TemplateQuickActions
                            template={template}
                            onView={() => setViewingTemplate(template)}
                            onEdit={() => handleEdit(template)}
                            onDelete={() => handleDelete(template.id)}
                            onDuplicate={handleDuplicateTemplate}
                            onSendTest={() => handleSendTestSMS(template)}
                            canEdit={isAdmin || canEditTemplate(user?.role)}
                            canDelete={isAdmin || canDeleteTemplate(user?.role)}
                            canSendTest={true}
                          />
                          {isAdmin && (user?.role === 'master_admin' || user?.role === 'MASTER_ADMIN') && (
                            <>
                              {template.is_active ? (
                                <button onClick={() => handleDeactivate(template)} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm" style={{ color: '#ea580c', background: 'rgba(249, 115, 22, 0.06)', border: '1px solid rgba(249, 115, 22, 0.12)' }} title="Deactivate">
                                  <XCircle className="w-3 h-3" />
                                </button>
                              ) : (
                                <button onClick={() => handleActivate(template)} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all hover:shadow-sm" style={{ color: '#059669', background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }} title="Activate">
                                  <CheckCircle className="w-3 h-3" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {totalRecords > 0 && (
            <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={totalRecords}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              />
            </div>
          )}
        </div>
      )}

      {/* View Template Modal */}
      {viewingTemplate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Template Details</h3>
                  <p className="text-xs text-gray-500">Full template information</p>
                </div>
              </div>
              <button onClick={() => setViewingTemplate(null)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Header', value: getHeaderName(viewingTemplate.header_id, viewingTemplate) },
                  { label: 'Template Type', value: getTypeLabel(viewingTemplate.template_type), badge: true },
                  { label: 'Template ID (DLT)', value: viewingTemplate.template_id, mono: true },
                  { label: 'Template Name', value: viewingTemplate.template_name },
                ].map((item, i) => (
                  <div key={i} className="rounded-lg p-2.5" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{item.label}</div>
                    {item.badge ? (
                      <span className={`px-2 py-1 inline-flex text-[11px] font-semibold rounded-full ${getTypeBadgeColor(viewingTemplate.template_type)}`}>
                        {item.value}
                      </span>
                    ) : (
                      <div className={`text-sm text-gray-800 break-all ${item.mono ? 'font-mono text-xs' : 'font-medium'}`}>
                        {item.value || '—'}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Template Content */}
              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template Content</div>
                <div className="p-3 rounded-xl text-sm text-gray-800 whitespace-pre-wrap break-all font-mono leading-relaxed" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)', minHeight: '80px' }}>
                  {viewingTemplate.template_content || '—'}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)', background: 'rgba(99, 102, 241, 0.02)' }}>
              <button
                onClick={() => setViewingTemplate(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:shadow-md"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Template Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.04), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  {editingTemplate ? <Pencil className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{editingTemplate ? 'Edit Template' : 'Add New Template'}</h3>
                  <p className="text-xs text-gray-500">{editingTemplate ? 'Update template details' : 'Create a new DLT template'}</p>
                </div>
              </div>
              <button onClick={handleCloseModal} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {/* PROMPT-111: Modal-scoped error display */}
            {modalError && (
              <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 whitespace-pre-line flex-1">{modalError}</p>
                <button onClick={() => setModalError('')} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {/* PROMPT-111: Modal-scoped success display */}
            {modalSuccess && (
              <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700 whitespace-pre-line flex-1">{modalSuccess}</p>
                <button onClick={() => setModalSuccess('')} className="text-green-400 hover:text-green-600 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {/* PROMPT-162: Duplicate Warning */}
            {duplicateWarning && (
              <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-700 font-medium">
                    {duplicateWarning.message} for {duplicateWarning.field === 'template_id' ? 'Template ID' : 'Template Name'}: "{duplicateWarning.value}"
                  </p>
                  <div className="mt-1.5">
                    <p className="text-xs text-yellow-600">Similar templates found:</p>
                    <ul className="mt-1 text-xs text-yellow-600 list-disc list-inside">
                      {duplicateWarning.templates.slice(0, 3).map((template, index) => (
                        <li key={index}>
                          {template.template_name} (ID: {template.template_id}) - {template.entity_name}
                        </li>
                      ))}
                      {duplicateWarning.templates.length > 3 && (
                        <li>... and {duplicateWarning.templates.length - 3} more</li>
                      )}
                    </ul>
                  </div>
                </div>
                <button onClick={() => setDuplicateWarning(null)} className="text-yellow-400 hover:text-yellow-600 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity *</div>
                  <SearchableDropdown
                    options={entityOptions.filter(o => o.value !== '')}
                    value={formData.entity_id}
                    onChange={handleEntityChange}
                    placeholder="Select entity..."
                    ref={entityDropdownRef}
                    required
                  />
                </div>

                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template Type *</div>
                  <SearchableDropdown
                    options={typeOptions.filter(o => o.value !== '')}
                    value={formData.template_type}
                    onChange={handleTemplateTypeChange}
                    placeholder="Select template type..."
                    ref={templateTypeDropdownRef}
                    required
                  />
                </div>

                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template ID (DLT) *</div>
                  <input
                    type="text"
                    required
                    value={formData.template_id}
                    onChange={handleTemplateIdChange}
                    onPaste={handleTemplateIdPaste}
                    onKeyDown={handleTemplateIdKeyDown}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Enter DLT template ID"
                    ref={templateIdInputRef}
                  />
                  <p className="mt-1 text-xs text-gray-400">Only numbers are allowed</p>
                </div>

                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template Name *</div>
                  <input
                    type="text"
                    required
                    value={formData.template_name}
                    onChange={(e) => {
                      setFormData({ ...formData, template_name: e.target.value });
                      // PROMPT-162: Check for duplicate template name
                      checkDuplicateTemplate('template_name', e.target.value);
                    }}
                    onKeyDown={handleTemplateNameKeyDown}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Enter template name"
                    ref={templateNameInputRef}
                  />
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Headers *</div>
                {loadingHeaders ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div><span className="text-sm">Loading headers...</span></div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <SearchableDropdown
                        options={availableHeaders.map(h => ({
                          value: h.id.toString(),
                          label: h.header_name
                        }))}
                        value=""
                        onChange={handleAddHeader}
                        placeholder="Select headers..."
                        ref={headerDropdownRef}
                      />
                    </div>
                    
                    {formData.header_ids.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.header_ids.map(headerId => (
                          <span
                            key={headerId}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}
                          >
                            {getHeaderNameById(headerId)}
                            <button
                              type="button"
                              onClick={() => handleRemoveHeader(headerId)}
                              className="hover:text-red-500 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Template Content *</div>
                <textarea
                  required
                  rows="4"
                  value={formData.template_content}
                  onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
                  onKeyDown={handleTemplateContentKeyDown}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono"
                  placeholder="Enter template content with variables like {#var#}"
                  ref={templateContentRef}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-gray-400">
                    Use {'{#var#}'} for dynamic variables
                  </p>
                  <button
                    type="button"
                    onClick={handleAddVariable}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:shadow-sm"
                    style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}
                    title="Insert dynamic variable ({#var#})"
                  >
                    <Plus className="w-3 h-3" />
                    Add Variable
                  </button>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white transition-all hover:bg-gray-50"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  ref={createButtonRef}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:shadow-md"
                  style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          fetchTemplates(currentPage, pageSize);
          setSuccess('Templates uploaded successfully');
          setTimeout(() => setSuccess(''), 3000);
        }}
      />

      {/* Bulk Upload Wizard */}
      <BulkUploadWizard
        isOpen={showBulkUploadWizard}
        onClose={() => setShowBulkUploadWizard(false)}
        onSuccess={() => {
          fetchTemplates(currentPage, pageSize);
          setSuccess('Templates imported successfully');
          setTimeout(() => setSuccess(''), 3000);
        }}
      />
    </div>
  );
};

export default SharedTemplates;
